import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { IconX as X } from '@tabler/icons-react';
import { fetchAdminEvent, fetchAdminOrganizations, updateAdminEvent } from '@/api/adminApi';
import VenuePicker from '@/components/venue-picker/VenuePicker';
import { btnAccent, btnGhost, input, label } from '@/components/ui/design';

type Form = {
    title: string; description: string; category: string; email: string; venue: string; handle: string;
    status: string; organizerId: string; isPrivate: boolean; commentsDisabled: boolean;
    refundable: boolean; refundDaysBeforeStart: string; isDeleted: boolean;
    feeMode: 'added' | 'included'; taxMode: 'added' | 'included'; currency: 'usd' | 'cad'; venueId: string;
};

const EMPTY: Form = {
    title: '', description: '', category: '', email: '', venue: '', handle: '', status: 'draft', organizerId: '',
    isPrivate: false, commentsDisabled: false, refundable: false, refundDaysBeforeStart: '0', isDeleted: false, feeMode: 'included', taxMode: 'included', currency: 'usd', venueId: '',
};

export default function EventEditor({ eventId, onClose }: { eventId: string; onClose: () => void }) {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useQuery({ queryKey: ['admin-event', eventId], queryFn: () => fetchAdminEvent(eventId) });
    const { data: organizations } = useQuery({
        queryKey: ['admin-organizations', 'all-for-select'],
        queryFn: () => fetchAdminOrganizations({ page: 1, limit: 100 }),
        staleTime: 60_000,
    });
    const [form, setForm] = useState<Form>(EMPTY);
    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm(prev => ({ ...prev, [key]: value }));

    useEffect(() => {
        if (!data) return;
        setForm({
            title: data.title ?? '', description: data.description ?? '', category: data.category ?? '', email: data.email ?? '',
            venue: data.venue ?? '', handle: data.handle ?? '', status: data.status ?? 'draft', organizerId: data.organizerId ? String(data.organizerId) : '',
            isPrivate: Boolean(data.isPrivate), commentsDisabled: Boolean(data.commentsDisabled),
            refundable: Boolean(data.refundable), refundDaysBeforeStart: String(data.refundDaysBeforeStart ?? 0), isDeleted: Boolean(data.isDeleted),
            feeMode: data.feeMode ?? 'included', taxMode: data.taxMode ?? 'included', currency: data.currency ?? 'usd', venueId: data.venueId ? String(data.venueId) : '',
        });
    }, [data]);

    const save = useMutation({
        mutationFn: () => updateAdminEvent(eventId, {
            title: form.title, description: form.description, category: form.category, email: form.email, venue: form.venue,
            handle: form.handle, status: form.status, isPrivate: form.isPrivate ? 1 : 0, commentsDisabled: form.commentsDisabled ? 1 : 0,
            refundable: form.refundable, refundDaysBeforeStart: Number(form.refundDaysBeforeStart), isDeleted: form.isDeleted, feeMode: form.feeMode, taxMode: form.taxMode, currency: form.currency, venueId: form.venueId || null,
            ...(form.organizerId ? { organizerId: form.organizerId } : {}),
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-events'] });
            queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
            queryClient.invalidateQueries({ queryKey: ['admin-report'] });
            addToast({ title: 'Saved', description: 'Event updated.', color: 'success' });
            onClose();
        },
        onError: (error: any) => {
            addToast({ title: 'Could not save', description: error?.response?.data?.message || error?.message || 'Please try again.', color: 'danger' });
        },
    });

    const check = (key: 'isPrivate' | 'commentsDisabled' | 'refundable' | 'isDeleted', text: string) => (
        <label className="flex items-center gap-3 text-sm font-semibold text-grey10 dark:text-white">
            <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="h-4 w-4" />{text}
        </label>
    );
    const orgs = organizations?.data ?? [];
    const orgMissing = form.organizerId && !orgs.some(o => o._id === form.organizerId);

    return (
        <div className="fixed inset-0 z-[9000] flex justify-end bg-black/50" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-label="Edit event" className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-nightSurface">
                <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/10">
                    <h2 className="truncate text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{data?.title || 'Edit event'}</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-5">
                    {isLoading && <p className="text-sm text-grey40 dark:text-nightMuted">Loading...</p>}
                    {isError && <p className="text-sm text-red-500">Failed to load the event.</p>}
                    {data && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <label className="sm:col-span-2"><span className={label}>Title</span><input value={form.title} onChange={e => set('title', e.target.value)} className={input} /></label>
                            <label className="sm:col-span-2"><span className={label}>Description</span><textarea rows={5} value={form.description} onChange={e => set('description', e.target.value)} className={`${input} h-auto py-3`} /></label>
                            <label><span className={label}>Category</span><input value={form.category} onChange={e => set('category', e.target.value)} className={input} /></label>
                            <label><span className={label}>Status</span>
                                <select value={form.status} onChange={e => set('status', e.target.value)} className={input}>
                                    <option value="draft">Draft</option><option value="published">Published</option><option value="completed">Completed</option>
                                </select>
                            </label>
                            <label><span className={label}>Handle</span><input value={form.handle} onChange={e => set('handle', e.target.value)} className={input} /></label>
                            <label><span className={label}>Contact email</span><input value={form.email} onChange={e => set('email', e.target.value)} className={input} /></label>
                            <label className="sm:col-span-2"><span className={label}>Venue name</span><input value={form.venue} onChange={e => set('venue', e.target.value)} className={input} /></label>
                            <div className="sm:col-span-2">
                                <span className={label}>Venue map</span>
                                <VenuePicker
                                    name={form.venue}
                                    venueId={form.venueId || null}
                                    allVenues
                                    organizerId={form.organizerId || undefined}
                                    onChange={next => setForm(prev => ({ ...prev, venue: next.name, venueId: next.venueId ?? '' }))}
                                />
                                <span className="mt-1 block text-xs text-grey40 dark:text-nightMuted">Picking a venue gives this event its own copy of it. The venue can't change after seats are sold.</span>
                            </div>
                            <label><span className={label}>Currency</span>
                                <select value={form.currency} onChange={e => set('currency', e.target.value as Form['currency'])} className={input}>
                                    <option value="usd">USD</option>
                                    <option value="cad">CAD</option>
                                </select>
                            </label>
                            <label className="sm:col-span-2"><span className={label}>Organization</span>
                                <select value={form.organizerId} onChange={e => set('organizerId', e.target.value)} className={input}>
                                    {!form.organizerId && <option value="">None</option>}
                                    {orgMissing && <option value={form.organizerId}>Current organization</option>}
                                    {orgs.map(o => <option key={o._id} value={o._id}>{o.orgName}</option>)}
                                </select>
                            </label>
                            <label><span className={label}>Refund days before start</span><input type="number" min="-1" value={form.refundDaysBeforeStart} onChange={e => set('refundDaysBeforeStart', e.target.value)} className={input} /></label>
                            <label><span className={label}>Service fee</span>
                                <select value={form.feeMode} onChange={e => set('feeMode', e.target.value as Form['feeMode'])} className={input}>
                                    <option value="included">Included in ticket price</option>
                                    <option value="added">Added on top (buyer pays)</option>
                                </select>
                            </label>
                            <div className="flex flex-col justify-end gap-3 sm:col-span-2">
                                {check('isPrivate', 'Private event')}
                                {check('commentsDisabled', 'Comments disabled')}
                                {check('refundable', 'Refundable')}
                                {check('isDeleted', 'Deleted (hidden from the site)')}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 border-t border-black/[0.06] px-5 py-4 dark:border-white/10">
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                    <button type="button" onClick={() => save.mutate()} disabled={!data || save.isPending} className={btnAccent}>{save.isPending ? 'Saving...' : 'Save changes'}</button>
                </div>
            </div>
        </div>
    );
}
